import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Stack,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PublicIcon from '@mui/icons-material/Public';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LandscapeIcon from '@mui/icons-material/Landscape';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';

export default function PlanetPrompts() {
  const { t } = useLocalization();
  const { token } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newPrompt, setNewPrompt] = useState('');

  const columns = [
    { 
      field: 'prompt_text', 
      headerName: t('planet_prompt_text'), 
      width: 600,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ 
            width: 32, 
            height: 32, 
            mr: 2, 
            bgcolor: 'primary.main'
          }}>
            <PublicIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Tooltip title={params.value}>
            <Typography 
              noWrap 
              sx={{ 
                fontWeight: 500,
                maxWidth: 500
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Tooltip title={t('delete_planet_prompt')}>
          <IconButton
            color="error"
            onClick={() => handleDelete(params.row.prompt_id)}
            sx={{ 
              '&:hover': {
                bgcolor: 'error.light',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/planet-prompts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrompts(response.data);
      setError('');
    } catch (err) {
      setError(t('failed_to_fetch_planet_prompts'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setError('');
      setSuccess('');
      const response = await axios.post('/planet-prompts', 
        { prompt_text: newPrompt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrompts([...prompts, response.data]);
      setOpen(false);
      setNewPrompt('');
      setSuccess(t('planet_prompt_added_successfully'));
      
      // Dispatch event to notify about planet prompt changes
      window.dispatchEvent(new CustomEvent('planetPromptsChanged'));
    } catch (err) {
      setError(t('failed_to_add_planet_prompt'));
    }
  };

  const handleDelete = async (promptId) => {
    if (window.confirm(t('confirm_delete_planet_prompt'))) {
      try {
        setError('');
        setSuccess('');
        await axios.delete(`/planet-prompts/${promptId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPrompts(prompts.filter(p => p.prompt_id !== promptId));
        setSuccess(t('planet_prompt_deleted_successfully'));
        
        // Dispatch event to notify about planet prompt changes
        window.dispatchEvent(new CustomEvent('planetPromptsChanged'));
      } catch (err) {
        setError(t('failed_to_delete_planet_prompt'));
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <PublicIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('planet_prompts_management')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('manage_planet_generation_prompts')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={12}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <PublicIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {prompts.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_planet_prompts')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('planet_prompt_library')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('view_manage_planet_prompts')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('add_planet_prompt')}
            </Button>
          </Box>
          
          <Box sx={{ height: 500, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
            <DataGrid
              rows={prompts}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              getRowId={(row) => row.prompt_id}
              disableSelectionOnClick
              autoWidth
              components={{
                NoRowsOverlay: () => (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    color: 'text.secondary',
                    fontSize: '1rem',
                    fontWeight: 500
                  }}>
                    {t('no_planet_prompts_found')}
                  </Box>
                )
              }}
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-columnHeader': {
                  flex: 1,
                },
                '& .MuiDataGrid-cell': {
                  flex: 1,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('add_new_planet_prompt')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3} sx={{ mt: 2, minWidth: '500px' }}>
            <TextField
              autoFocus
              label={t('planet_prompt_text')}
              fullWidth
              multiline
              rows={6}
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              helperText={t('planet_prompt_text_placeholder')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleAdd} 
            variant="contained"
            disabled={!newPrompt.trim()}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {t('add_planet_prompt_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 